import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { schedules, locations } from '../data/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
  isWithinInterval,
} from 'date-fns';

const MySchedule = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());

  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const mySchedules = useMemo(() => {
    return schedules.filter((s) => s.employeeId === user?.id);
  }, [user]);

  const weekSchedules = useMemo(() => {
    return mySchedules.filter((s) => {
      const schedDate = parseISO(s.date);
      return isWithinInterval(schedDate, { start: weekStart, end: weekEnd });
    });
  }, [mySchedules, weekStart, weekEnd]);

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const location = locations.find((l) => l.id === user?.locationId);

  const shiftColors = {
    Morning: 'bg-amber-100 border-amber-200 text-amber-800',
    Mid: 'bg-blue-100 border-blue-200 text-blue-800',
    Evening: 'bg-purple-100 border-purple-200 text-purple-800',
  };

  // Calculate weekly hours
  const weeklyHours = weekSchedules.reduce((total, sched) => {
    const [startH, startM] = sched.startTime.split(':').map(Number);
    const [endH, endM] = sched.endTime.split(':').map(Number);
    const hours = (endH + endM / 60) - (startH + startM / 60);
    return total + hours;
  }, 0);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Schedule</h1>
        <p className="text-gray-500 mt-1">
          View your upcoming shifts at {location?.name || 'your location'}
        </p>
      </div>

      {/* Week Navigation */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={handlePrevWeek}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={handleToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextWeek}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">This week:</span>
              <span className="font-semibold text-purple-600">
                {weekSchedules.length} shifts • {weeklyHours.toFixed(1)}h
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Week View */}
      <div className="grid grid-cols-7 gap-4">
        {weekDays.map((day) => {
          const daySchedules = weekSchedules.filter(
            (s) => s.date === format(day, 'yyyy-MM-dd')
          );
          const isToday = isSameDay(day, new Date());

          return (
            <Card
              key={day.toISOString()}
              className={`border-0 shadow-md ${
                isToday ? 'ring-2 ring-purple-500' : ''
              }`}
            >
              <CardHeader className="pb-2">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">
                    {format(day, 'EEE')}
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isToday ? 'text-purple-600' : 'text-gray-900'
                    }`}
                  >
                    {format(day, 'd')}
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {daySchedules.length > 0 ? (
                  <div className="space-y-2">
                    {daySchedules.map((sched) => (
                      <div
                        key={sched.id}
                        className={`p-3 rounded-lg border ${shiftColors[sched.shiftName]}`}
                      >
                        <p className="font-medium text-sm">
                          {sched.startTime} - {sched.endTime}
                        </p>
                        <p className="text-xs mt-1">{sched.shiftName} Shift</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 text-sm py-4">Off</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Legend */}
      <Card className="mt-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex items-center gap-6">
            <span className="text-sm text-gray-500">Shift Types:</span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-100 border border-amber-200" />
                <span className="text-sm text-gray-600">Morning (6am-2pm)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200" />
                <span className="text-sm text-gray-600">Mid (10am-6pm)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-purple-100 border border-purple-200" />
                <span className="text-sm text-gray-600">Evening (2pm-10pm)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MySchedule;
